/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Component, OnInit, Renderer2} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {ActivatedRoute, Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Removal, RemovalPoint, ThirdParty} from '../../_models/models';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {
  AnimalService,
  AuthService,
  ExternalImportRemovalService,
  I18nService,
  ThirdPartyService
} from '../../_services/services';
import {DialogHelper} from '../../_helpers/dialog-helper';
import {Helpers} from '../../helpers';
import {ExternalImportRemoval} from '../../_models/external-import-removal';

import {NOT_FOUND, REQUEST_TIME_OUT, UNAUTHORIZED} from '../../_enums/requests';
import {DEFAULT, EMBEDDED} from '../../_enums/serializer-groups';
import {
  BOVICLIC,
  BOVICLIC_JWT_KEY,
  ERROR_KIND_CODE_MESSAGE,
  ERROR_MESSAGE_IS_ARRAY,
  HOME_PATH
} from '../../_enums/constants';

@Component({
  selector: 'app-boviclic',
  templateUrl: './boviclic.component.html',
  styleUrls: ['./boviclic.component.scss']
})
export class BoviclicComponent implements OnInit {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private decodeJWT: any;

  public rp: RemovalPoint;
  public tp: ThirdParty;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS GROUPS                                                                                          ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public removalFG: FormGroup;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGICS METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    private activatedRoute: ActivatedRoute,
    private animalService: AnimalService,
    private dialogHelper: DialogHelper,
    private eRIService: ExternalImportRemovalService,
    private formBuilder: FormBuilder,
    private i18n: I18nService,
    private renderer: Renderer2,
    private router: Router,
    private thirdPartyService: ThirdPartyService,
    private toastr: ToastrService
  ) {
    this.renderer.setStyle(document.body, 'background-color', 'rgba(117, 54, 230, .1)');
  }

  ngOnInit() {
    this.initRemovalForm();
    this.checkJWT();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              REMOVAL FORM CONTROLS                                                                                 ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get thirdPartyFC(): AbstractControl {
    return this.removalFG.get('thirdParty');
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get data(): string | null {
    if (this.activatedRoute.snapshot.queryParams.data) {
      return String(this.activatedRoute.snapshot.queryParams.data);
    }

    if (this.activatedRoute.snapshot.params.data) {
      return String(this.activatedRoute.snapshot.params.data);
    }

    return null;
  }

  get animalIds(): number[] {
    return this.decodeJWT ? this.decodeJWT['animals'] : [];
  }

  get hq(): RemovalPoint {
    if (this.tp && this.tp._embedded && this.tp._embedded.headquarter) {
      return this.tp._embedded.headquarter;
    }

    return null;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS INITIALISATION                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private initRemovalForm() {
    this.removalFG = this.formBuilder.group({
      callBeforeTime: null,
      comments: [null, [Validators.maxLength(120)]],
      office: [BOVICLIC, [Validators.required]],
      thirdParty: [null, [Validators.required]],
      removalPoint: [null, [Validators.required]],
      animals: this.formBuilder.array([], Validators.required)
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS SUBMITS                                                                                         ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public removalFormSubmit() {
    if (this.removalFG.valid) {
      Helpers.setLoading(true);

      this.eRIService.create(this.removalFG.value, {
        serializerGroups: [DEFAULT, EMBEDDED],
        version: 0.3
      }).subscribe((res: HttpResponse<ExternalImportRemoval>) => {
        Helpers.setLoading(false);

        this.dialogHelper.showRemovalValidate(this.removalFG.value, res.body).subscribe((e?: Removal) => {
          if (e) {
            this.animalService.deleteBoviclicAnimals(this.animalIds);

            this.dialogHelper.showRemovalFinished(e).subscribe(() => {
              this.router.navigate([HOME_PATH]);
            });
          } else {
            this.router.navigate([HOME_PATH]);
          }
        });
      }, (err: HttpErrorResponse) => this.handleError(err));
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                                     LISTENERS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public cancel(): void {
    this.dialogHelper.showBoviclicCancel().subscribe((res: boolean) => {
      if (res) {
        this.animalService.deleteBoviclicAnimals(this.animalIds);
      }
    });
  }

  public rpChange(rp: RemovalPoint) {
    this.rp = rp;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PRIVATE METHODS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private checkJWT() {
    if (!this.data) {
      this.showDialogError(NOT_FOUND);
    } else {
      const jwtHelper = new JwtHelperService();
      this.decodeJWT = jwtHelper.decodeToken(this.data);
      const isExpired = jwtHelper.isTokenExpired(this.data);
      if (isExpired) {
        this.showDialogError(REQUEST_TIME_OUT);
      } else {
        // Store in local storage the auth token received in the JWT to make requests to the API
        if (this.decodeJWT['token']) {
          AuthService.storeAuthToken(BOVICLIC_JWT_KEY, this.decodeJWT['token']);
          this.initThirdParty();
          this.decodeJWT?.referer && this.removalFG.patchValue({ 'office': `internet_${this.decodeJWT?.referer}`})
        } else {
          this.showDialogError(UNAUTHORIZED);
        }
      }
    }
  }

  private handleError(err: HttpErrorResponse): void {
    Helpers.setLoading(false);
    const e = err.error;

    if (e.hasOwnProperty('code') && ERROR_MESSAGE_IS_ARRAY === e.code && e.message.hasOwnProperty('kind') && ERROR_KIND_CODE_MESSAGE === e.message.kind) {
      this.handleErrorCodeMessage(e.message.errors);
    } else {
      this.toastr.error(`${err.status} - ${this.i18n.get(err.status)}`);
    }
  }

  private handleErrorCodeMessage(errors: { code: number, message: string }[]): void {
    for (const error of errors) {
      this.toastr.error(error.message);
    }
  }

  private initThirdParty() {
    if (this.decodeJWT['thirdPartyId']) {
      this.thirdPartyService.getById(Number(this.decodeJWT['thirdPartyId']), {
        serializerGroups: [DEFAULT, EMBEDDED]
      }).subscribe((res: HttpResponse<ThirdParty>) => {
        if (res.body.locked) {
          this.tpLocked();
        } else {
          this.tp = res.body;
          this.thirdPartyFC.setValue(this.tp.id);
        }
      }, (err: HttpErrorResponse) => this.handleError(err));
    } else {
      this.showDialogError(NOT_FOUND);
    }
  }

  private showDialogError(code: number): void {
    this.dialogHelper.showExternalRemovalRequest({showContact: false, message: this.i18n.get(code), referer: this.decodeJWT?.referer})
  }

  private tpLocked(): void {
    this.dialogHelper.showThirdPartyLocked().subscribe(() => {
      this.router.navigate([HOME_PATH]);
    });
  }

}
